import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { CreateSpaceData, useCreateSpaceV2 } from '@zep/apis';
import { redirectSigninPage } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { SpaceUsageType } from '@zep/types';

import { CreateSpaceSchemaType } from '../schema';

interface UseCreateSpaceOptions {
  onSuccess?: (spaceId: string, entryMapHashId: string) => void;
  analyticsEvent?: string;
  place?: 'play' | 'home';
}

export function useCreateSpace(options: UseCreateSpaceOptions = {}) {
  const { onSuccess, analyticsEvent, place } = options;
  const { t } = useTranslation(['common']);
  const dialog = useDialog();
  const createSpaceMutation = useCreateSpaceV2();

  const createSpace = useCallback(
    async (
      data: CreateSpaceSchemaType,
      template: { hashId: string; name: string; isPurchased?: boolean },
    ) => {
      try {
        // 요청 객체 생성
        const request: CreateSpaceData = {
          name: data.name.trim(),
          spaceUsageType: SpaceUsageType.PUBLIC,
          enablePassword: false,
          enableRtc: data.enableRtc,
        };

        // 템플릿 설정
        if (template.isPurchased) {
          request.purchasedTemplateHashId = template.hashId;
        } else {
          request.templateHashId = template.hashId;
        }

        // 스페이스 생성 API 호출
        const space = await createSpaceMutation.mutateAsync(request);

        // Analytics 이벤트
        if (analyticsEvent) {
          const eventProps = {
            map_template_id: template.hashId,
            template_name: template.name,
            place: place || 'play',
            space_name: space.name,
            spaceUsageType:
              space.options.spaceUsageType === SpaceUsageType.PRIVATE
                ? 'private'
                : 'public',
            password: space.options.enablePassword ? 'true' : 'false',
            category: space.categoryIds.map(String),
            rtc: data.enableRtc ? 'on' : 'off',
          };

          logger.track(analyticsEvent, eventProps);
        }

        // 성공 콜백 호출
        if (onSuccess) {
          onSuccess(space.hashId, space.entryMapHashId);
        }

        return space;
      } catch (error) {
        // 호출부(react-hook-form handleSubmit)는 거부를 받지 않는다. 다시 던지면
        // unhandledrejection 이 되므로 여기서 보고하고 끝낸다.
        logger.captureError(error, { tags: { source: 'createSpace' } });

        if (isAuthError(error)) {
          await dialog.alert({ title: t('common:alert.signin') });
          redirectSigninPage();
          return undefined;
        }

        await dialog.alert({
          title: t('common:error.api.unknown'),
        });
        return undefined;
      }
    },
    [createSpaceMutation, dialog, analyticsEvent, onSuccess, place, t],
  );

  return {
    createSpace,
    isLoading: createSpaceMutation.isLoading,
  };
}

const AUTH_ERROR_STATUSES = new Set([401, 403]);

const isAuthError = (error: unknown) =>
  axios.isAxiosError(error) &&
  AUTH_ERROR_STATUSES.has(error.response?.status ?? 0);
